import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';

export default async function WasamaCustomerLoanDetailsExcel(
  data,
  selectedBranchIds,
  selectedWasamaIds,
  selectedProductIds,
  generatedAt = new Date()
) {
  if (!data || !Array.isArray(data) || data.length === 0) {
    console.warn("No data provided for Wasama-wise Customer Loan Details Excel");
    return;
  }

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Wasama-wise Loan Details');

  const totalColumns = 11;

  // === TITLE & FILTER SUMMARY ===
  let rowIndex = 1;

  // Main Title
  sheet.mergeCells(rowIndex, 1, rowIndex, totalColumns);
  const titleCell = sheet.getCell(`A${rowIndex}`);
  titleCell.value = 'Wasama-wise Customer Loan Details Report';
  titleCell.font = { bold: true, size: 18, color: { argb: '000080' } };
  titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
  titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'DDEBF7' } };
  sheet.getRow(rowIndex).height = 50;
  rowIndex++;

  // Subtitle
  sheet.mergeCells(rowIndex, 1, rowIndex, totalColumns);
  const subTitle = sheet.getCell(`A${rowIndex}`);
  subTitle.value = 'All Loans (Active, Non-Performing & Closed)';
  subTitle.font = { bold: true, size: 14 };
  subTitle.alignment = { horizontal: 'center' };
  sheet.getRow(rowIndex).height = 35;
  rowIndex++;

  // Generated Date
  sheet.mergeCells(rowIndex, 1, rowIndex, totalColumns);
  const generatedCell = sheet.getCell(`A${rowIndex}`);
  generatedCell.value = `Generated on: ${dayjs(generatedAt).format('YYYY-MM-DD HH:mm')}`;
  generatedCell.font = { italic: true, size: 11 };
  generatedCell.alignment = { horizontal: 'center' };
  sheet.getRow(rowIndex).height = 25;
  rowIndex++;

  // Filter Summary
  rowIndex++;
  sheet.addRow(['Branches:', selectedBranchIds?.length ? selectedBranchIds.join(', ') : 'All Branches']);
  sheet.addRow(['Wasamas:', selectedWasamaIds?.length ? selectedWasamaIds.join(', ') : 'All Wasamas']);
  sheet.addRow(['Products:', selectedProductIds?.length ? selectedProductIds.join(', ') : 'All Products']);
  rowIndex += 4;

  // === CONTENT: Loop through each Wasama ===
  data.forEach((wasama, wasamaIndex) => {
    // Wasama Header
    sheet.addRow([`Wasama: ${wasama.wasamaName || 'Unknown'} (ID: ${wasama.wasamaId || '-'})`]);
    const wasamaRow = sheet.getRow(rowIndex);
    wasamaRow.font = { bold: true, size: 14, color: { argb: '1F497D' } };
    sheet.mergeCells(`A${rowIndex}:K${rowIndex}`);
    rowIndex++;

    // Customers under this Wasama
    (wasama.customers || []).forEach((customer, custIndex) => {
      // --- TABLE 1: Customer Details (Horizontal) ---
      const custHeaders = [
        'Customer Number',
        'Customer Name',
        'NIC ID',
        'Contact Number',
        'Address',
        'Customer Type',
        'Branch Name'
      ];

      const custHeaderRow = sheet.addRow(custHeaders);
      custHeaderRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: '333333' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F2F2F2' } };
        cell.border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
      });
      rowIndex++;

      const custDataRow = sheet.addRow([
        customer.customerNumber || '-',
        customer.customerName || '-',
        customer.nicId || '-',
        customer.contactNumber || '-',
        customer.address || '-',
        customer.customerType || '-',
        customer.branchName || '-',
      ]);
      custDataRow.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
      });
      rowIndex++;

      sheet.addRow([]);
      rowIndex++;

      // --- TABLE 2: Loan Details ---
      const headers = [
        'Loan No.',
        'Loan A/C No',
        'Product Name',
        'Loan Type',
        'Loan Amount',
        'Disbursed Amount',
        'Total Recovered',
        'Outstanding Amount',
        'Interest Rate (%)',
        'Loan Status',
        'Disbursement Date',
      ];

      const headerRow = sheet.addRow(headers);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '4472C4' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
      });
      rowIndex++;

      // Loans Data
      let zebraIndex = 0;
      const sortedLoans = [...(customer.loans || [])].sort((a, b) => {
        const order = { active: 0, 'non-performing': 1, closed: 2 };
        return (order[a.loanStatus?.toLowerCase()] || 1) - (order[b.loanStatus?.toLowerCase()] || 1);
      });

      if (sortedLoans.length > 0) {
        sortedLoans.forEach((loan) => {
          const status = loan.loanStatus?.toLowerCase() || '';
          let outstanding = 0;
          if (status !== 'closed') {
            const calc = (loan.disbursedAmount || 0) - (loan.totalRecovered || 0);
            outstanding = Math.max(0, calc);
          }

          const row = sheet.addRow([
            loan.loanNumber || '-',
            loan.loanAccountNumber || '-',
            loan.productName || '-',
            loan.loanTypeName || '-',
            loan.loanAmount || 0,
            loan.disbursedAmount || 0,
            loan.totalRecovered || 0,
            outstanding,
            loan.interestRate || '-',
            loan.loanStatus || '-',
            loan.disbursementDate ? dayjs(loan.disbursementDate).format('YYYY-MM-DD') : '-',
          ]);

          const zebraColor = zebraIndex % 2 === 0 ? 'FFFFFF' : 'F2F2F2';
          row.eachCell((cell, colNum) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: zebraColor } };
            cell.border = {
              top: { style: 'thin' },
              bottom: { style: 'thin' },
              left: { style: 'thin' },
              right: { style: 'thin' },
            };

            // Number formatting
            if ([5, 6, 7, 8, 9].includes(colNum)) {
              cell.numFmt = colNum === 9 ? '0.00' : '#,##0.00';
            }
          });

          zebraIndex++;
          rowIndex++;
        });
      } else {
        sheet.addRow(['No loans found for this customer']);
        sheet.mergeCells(`A${rowIndex}:K${rowIndex}`);
        sheet.getCell(`A${rowIndex}`).alignment = { horizontal: 'center' };
        sheet.getCell(`A${rowIndex}`).font = { italic: true, color: { argb: '808080' } };
        rowIndex++;
      }

      sheet.addRow([]);
      sheet.addRow([]);
      rowIndex += 2;
    });

    // Spacer between Wasama sections
    if (wasamaIndex < data.length - 1) {
      sheet.addRow([]);
      sheet.addRow([]);
      rowIndex += 2;
    }
  });

  // === COLUMN WIDTHS ===
  sheet.columns = [
    { width: 22 },  // Loan No.
    { width: 30 },  // Loan A/C No
    { width: 35 },  // Product Name
    { width: 45 },  // Loan Type (also good for address/customer name)
    { width: 25 },  // Loan Amount
    { width: 25 },  // Disbursed Amount
    { width: 22 },  // Total Recovered
    { width: 25 },  // Outstanding Amount
    { width: 20 },  // Interest Rate
    { width: 22 },  // Status
    { width: 22 },  // Date
  ];

  // Freeze top rows (title + filters)
  sheet.views = [{ state: 'frozen', ySplit: 6 }];

  // === EXPORT ===
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const filename = `Wasama_Customer_Loan_Details_${dayjs(generatedAt).format('YYYYMMDD_HHmm')
    }.xlsx`;

  saveAs(blob, filename);
}